import { Icon } from '@iconify/react'
import { safeExternalUrl } from '@/utils/safeExternalUrl'

/**
 * VenueLink — the venue / host organisation name, optionally linking to its
 * official website.
 *
 * Added 2026-10-20. A lecture is usually held at a school, and the visitor's
 * natural next question is "what is this school?" — so the venue name links
 * straight to the school's own site, opening in a new tab so the visitor does
 * not lose their place in the lecture list.
 *
 * WHY A SEPARATE COMPONENT
 * ------------------------
 * The same venue string is rendered in two very different DOM contexts:
 *
 *   1. the `compact` card, where the ENTIRE card is one <Link> to the lecture
 *      detail page, and
 *   2. the `regular` card / detail page, where the venue sits in a plain
 *      <span> with no wrapping link.
 *
 * An <a> nested inside an <a> is invalid HTML — the browser's HTML parser
 * closes the outer anchor at the first inner `</a>`, which silently breaks the
 * whole card. So on the compact card the venue must NOT be an anchor.
 *
 * Instead of guessing, this component takes the decision explicitly:
 *
 *   - `linkable` (default true) → renders an <a target="_blank">
 *   - `linkable={false}`       → renders plain text, for use inside a
 *     wrapping <Link>
 *
 * The URL is validated by `safeExternalUrl` before it is used: a value that
 * is not an http(s) URL is rendered as plain text, so a bad database value
 * can never turn into a `javascript:` href.
 */

interface VenueLinkProps {
  /** The venue name, e.g. "余振強紀念中學". */
  venue: string
  /** The organisation's official website, e.g. "https://www.yckmc.edu.hk/". */
  url?: string
  /**
   * Whether the venue may render as an anchor. Set false when the component
   * sits inside another anchor (the compact card wraps everything in a Link).
   */
  linkable?: boolean
  /** Extra classes for the wrapper. */
  className?: string
  /** Extra classes for the anchor (ignored when not linkable). */
  linkClassName?: string
  /** i18n label appended to the accessible name, e.g. "（外部連結）". */
  externalLabel?: string
}

export default function VenueLink({
  venue,
  url,
  linkable = true,
  className = '',
  linkClassName = '',
  externalLabel,
}: VenueLinkProps) {
  const href = linkable ? safeExternalUrl(url) : undefined

  if (!href) {
    return <span className={className}>{venue}</span>
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={externalLabel ? `${venue} ${externalLabel}` : undefined}
      className={`${className} ${linkClassName}`.trim()}
    >
      <span>{venue}</span>
      <Icon icon="lucide:external-link" className="text-[0.7em] shrink-0" />
    </a>
  )
}
